import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Order, orderService } from '../../services/orderService';

const OrderManagement: React.FC = () => {
  const location = useLocation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [customerName, setCustomerName] = useState<string>('');
  const ordersPerPage = 10;

  useEffect(() => {
    // Check for customer filter in URL parameters
    const params = new URLSearchParams(location.search);
    const customer = params.get('customer');
    const name = params.get('customerName');
    
    if (customer) {
      setCustomerFilter(customer);
      setCustomerName(name || '');
      console.log('🔍 Setting customer filter:', customer, 'for customer:', name);
    }
    
    fetchOrders();
  }, [location.search]);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const result = await orderService.getAllOrders();
      if (result.success && result.data) {
        setOrders(result.data);
      } else {
        setError(result.error || 'Failed to fetch orders');
      }
    } catch (error) {
      setError('Failed to fetch orders');
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };


  const handleOrderAction = async (orderId: string, action: 'complete' | 'cancel' | 'refund' | 'retry') => {
    try {
      let updateData: any = {};
      
      if (action === 'complete') {
        updateData = { status: 'completed', paymentStatus: 'paid' };
      } else if (action === 'cancel') {
        if (!window.confirm('Are you sure you want to cancel this order?')) return;
        updateData = { status: 'cancelled' };
      } else if (action === 'refund') {
        if (!window.confirm('Are you sure you want to refund this order?')) return;
        updateData = { status: 'refunded', paymentStatus: 'refunded' };
      } else if (action === 'retry') {
        updateData = { status: 'processing', paymentStatus: 'pending' };
      }

      const result = await orderService.updateOrderStatus(orderId, updateData);
      if (result.success) {
        await fetchOrders(); // Refresh the orders list
      } else {
        setError(result.error || `Failed to ${action} order`);
      }
    } catch (error) {
      console.error(`Error performing ${action} on order:`, error);
      setError(`Failed to ${action} order`);
    }
  };

  // Helper function to get order type badge color
  const getOrderTypeBadgeColor = (orderType: string) => {
    switch (orderType) {
      case 'domain':
        return 'bg-blue-100 text-blue-800';
      case 'email':
        return 'bg-purple-100 text-purple-800';
      case 'hosting':
        return 'bg-green-100 text-green-800';
      case 'vps':
        return 'bg-orange-100 text-orange-800';
      case 'dedicated':
        return 'bg-red-100 text-red-800';
      case 'cloud':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Helper function to determine order type
  const getOrderType = (order: any) => {
    // Check for domain-only orders
    if (order.package_id === 'domain-only') {
      return 'domain';
    }
    
    // Check package_name from the JOIN query (from hosting_packages table)
    if (order.package_name) {
      const packageName = order.package_name.toLowerCase();
      if (packageName.includes('vps')) return 'vps';
      if (packageName.includes('email')) return 'email';
      if (packageName.includes('hosting') || packageName.includes('shared')) return 'hosting';
      if (packageName.includes('dedicated')) return 'dedicated';
      if (packageName.includes('cloud')) return 'cloud';
    }
    
    // Fallback: Check package_id patterns
    if (order.package_id) {
      const packageId = order.package_id.toLowerCase();
      if (packageId.includes('vps')) return 'vps';
      if (packageId.includes('email')) return 'email';
      if (packageId.includes('dedicated')) return 'dedicated';
      if (packageId.includes('cloud')) return 'cloud';
    }
    
    // Check items array for more specific type detection
    if (order.items) {
      const items = Array.isArray(order.items) ? order.items : [];
      
      // VPS orders
      if (items.some((item: any) => item.type === 'vps')) return 'vps';
      
      // Email package orders
      if (items.some((item: any) => item.type === 'email')) return 'email';
      
      // Domain-only orders (no hosting/vps items)
      if (items.some((item: any) => item.type === 'domain') && 
          !items.some((i: any) => ['hosting', 'vps', 'email'].includes(i.type))) {
        return 'domain';
      }
      
      // Hosting orders
      if (items.some((item: any) => item.type === 'hosting')) return 'hosting';
    }
    
    // Default to hosting if we can't determine
    return 'hosting';
  };

  // Debug: Log first time to see the filter and all available user_id values
  useEffect(() => {
    if (customerFilter && orders.length > 0) {
      console.log('🔎 Customer Filter Applied:', customerFilter);
      console.log('🔎 Total orders loaded:', orders.length);

      // Find all unique user_id values in orders
      const userIds = new Set<string>();
      const customerIds = new Set<string>();
      orders.forEach(o => {
        if ((o as any).user_id) userIds.add(String((o as any).user_id));
        if (o.customer_id) customerIds.add(String(o.customer_id));
      });
      console.log('🔎 Unique user_ids in orders:', Array.from(userIds).slice(0, 10));
      console.log('🔎 Unique customer_ids in orders:', Array.from(customerIds).slice(0, 10));

      // Check if the filter value exists in any orders
      const filterLower = customerFilter.toLowerCase();
      const matchingOrders = orders.filter(o =>
        String((o as any).user_id || '').toLowerCase() === filterLower ||
        String(o.customer_id || '').toLowerCase() === filterLower
      );
      console.log('🔎 Orders matching filter:', matchingOrders.length);
      if (matchingOrders.length > 0) {
        console.log('🔎 First matching order:', matchingOrders[0].order_number);
      }
    }
  }, [customerFilter, orders]);

  const filteredOrders = orders.filter(order => {
    const customerEmail = (order as any).customer_email || '';
    const packageName = (order as any).package_name || '';
    const matchesSearch = customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         packageName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         order.order_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (order.domain_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === '' || order.status === selectedStatus;
    const orderType = getOrderType(order);
    const matchesType = selectedType === '' || orderType === selectedType;

    // Customer filter based on URL parameter - handle UUID, string, and number comparison
    // The customerFilter from URL could be either customer.id (from customers table)
    // or user.id (from users table), so we check both
    let matchesCustomer = customerFilter === '';
    if (!matchesCustomer) {
      const orderCustomerId = String(order.customer_id || '').toLowerCase();
      const filterValue = customerFilter.toLowerCase();
      const orderUserId = String((order as any).user_id || '').toLowerCase();

      matchesCustomer = orderCustomerId === filterValue ||
                       orderUserId === filterValue;
    }

    return matchesSearch && matchesStatus && matchesType && matchesCustomer;
  });

  const indexOfLastOrder = currentPage * ordersPerPage;
  const indexOfFirstOrder = indexOfLastOrder - ordersPerPage;
  const currentOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder);
  const totalPages = Math.ceil(filteredOrders.length / ordersPerPage);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      case 'refunded':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      case 'refunded':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const calculateStats = () => {
    const totalRevenue = orders
      .filter(order => order.payment_status === 'paid')
      .reduce((sum, order) => sum + Number(order.total_amount), 0);
    
    // Calculate order type counts
    const orderTypeCounts = {
      hosting: 0,
      vps: 0,
      dedicated: 0,
      cloud: 0,
      email: 0,
      domain: 0
    };
    
    orders.forEach(order => {
      const type = getOrderType(order);
      if (orderTypeCounts[type] !== undefined) {
        orderTypeCounts[type]++;
      }
    });
    
    return {
      totalOrders: orders.length,
      completedOrders: orders.filter(o => o.status === 'completed').length,
      pendingOrders: orders.filter(o => o.status === 'pending').length,
      processingOrders: orders.filter(o => o.status === 'processing').length,
      totalRevenue,
      ...orderTypeCounts
    };
  };

  const stats = calculateStats();

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Order Management</h2>
          <p className="text-gray-600">Track and manage customer orders</p>
        </div>
        <div className="flex space-x-3">
          <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200">
            Export Orders
          </button>
          <button className="px-4 py-2 bg-primary-navy text-white rounded-lg hover:bg-primary-800 transition-colors duration-200">
            New Order
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Customer Filter Banner */}
      {customerFilter && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
              <span className="text-sm font-medium text-blue-800">
                Showing orders for customer: <strong>{customerName}</strong> (ID: {customerFilter})
                <br />
                <span className="text-xs">Total orders found: {filteredOrders.length} | All orders: {orders.length}</span>
              </span>
            </div>
            <button 
              onClick={() => {
                setCustomerFilter('');
                setCustomerName('');
                window.history.pushState({}, '', '/admin/orders');
              }}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
            >
              Clear Filter
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-bold text-gray-900">{stats.completedOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Processing</p>
              <p className="text-2xl font-bold text-gray-900">{stats.processingOrders}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Revenue</p>
              <p className="text-2xl font-bold text-gray-900">${Number(stats.totalRevenue).toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search orders..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
              />
            </div>
            <div className="w-full md:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="processing">Processing</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>
            </div>
            <div className="w-full md:w-48">
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
              >
                <option value="">All Types</option>
                <option value="hosting">Hosting</option>
                <option value="vps">VPS</option>
                <option value="dedicated">Dedicated</option>
                <option value="cloud">Cloud</option>
                <option value="email">Email</option>
                <option value="domain">Domain</option>
              </select>
            </div>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Order ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Item
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentOrders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{order.order_number}</div>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getOrderTypeBadgeColor(getOrderType(order))}`}>
                        {getOrderType(order)}
                      </span>
                      {order.billing_cycle && (
                        <span className="text-sm text-gray-500 capitalize">{order.billing_cycle}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-primary-navy flex items-center justify-center">
                        <span className="text-xs font-medium text-white">
                          {((order as any).customer_email || 'U').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="ml-3">
                        <div className="text-sm font-medium text-gray-900">
                          {(order as any).first_name} {(order as any).last_name}
                        </div>
                        <div className="text-sm text-gray-500">{(order as any).customer_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {(() => {
                      const orderType = getOrderType(order);
                      const packageName = (order as any).package_name;
                      
                      // Get the display name based on order type
                      const getDisplayName = () => {
                        if (packageName) return packageName;
                        
                        switch (orderType) {
                          case 'domain': return 'Domain Registration';
                          case 'hosting': return 'Hosting Package';
                          case 'vps': return 'VPS Package';
                          case 'dedicated': return 'Dedicated Server';
                          case 'cloud': return 'Cloud Package';
                          case 'email': return 'Email Package';
                          default: return 'Service Package';
                        }
                      };
                      
                      return (
                        <div>
                          <div className="text-sm text-gray-900">{getDisplayName()}</div>
                          <div className="text-sm text-gray-500">
                            {order.domain_name ? `Domain: ${order.domain_name}` : 'No domain specified'}
                          </div>
                          {order.billing_cycle && (
                            <div className="text-xs text-gray-400 mt-1">
                              Billing: {order.billing_cycle}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">${Number(order.total_amount).toFixed(2)} {order.currency}</div>
                    <div className="text-sm text-gray-500">{order.payment_method || 'N/A'}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getPaymentStatusColor(order.payment_status)}`}>
                      {order.payment_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(order.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end space-x-2">
                      {order.status === 'pending' && (
                        <button
                          onClick={() => handleOrderAction(order.id, 'complete')}
                          className="text-green-600 hover:text-green-900 text-sm"
                        >
                          Complete
                        </button>
                      )}
                      {order.status === 'processing' && (
                        <button
                          onClick={() => handleOrderAction(order.id, 'complete')}
                          className="text-green-600 hover:text-green-900 text-sm"
                        >
                          Complete
                        </button>
                      )}
                      {order.payment_status === 'paid' && order.status === 'completed' && (
                        <button
                          onClick={() => handleOrderAction(order.id, 'refund')}
                          className="text-purple-600 hover:text-purple-900 text-sm"
                        >
                          Refund
                        </button>
                      )}
                      {order.status !== 'completed' && order.status !== 'cancelled' && order.status !== 'refunded' && (
                        <button
                          onClick={() => handleOrderAction(order.id, 'cancel')}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {indexOfFirstOrder + 1} to {Math.min(indexOfLastOrder, filteredOrders.length)} of {filteredOrders.length} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm bg-primary-navy text-white rounded">
                  {currentPage}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Type Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Type Breakdown</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="text-2xl font-bold text-green-600">{stats.hosting}</div>
            <div className="text-sm text-gray-600">Hosting</div>
          </div>
          <div className="text-center p-4 bg-orange-50 rounded-lg">
            <div className="text-2xl font-bold text-orange-600">{stats.vps}</div>
            <div className="text-sm text-gray-600">VPS</div>
          </div>
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="text-2xl font-bold text-red-600">{stats.dedicated}</div>
            <div className="text-sm text-gray-600">Dedicated</div>
          </div>
          <div className="text-center p-4 bg-indigo-50 rounded-lg">
            <div className="text-2xl font-bold text-indigo-600">{stats.cloud}</div>
            <div className="text-sm text-gray-600">Cloud</div>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <div className="text-2xl font-bold text-purple-600">{stats.email}</div>
            <div className="text-sm text-gray-600">Email</div>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="text-2xl font-bold text-blue-600">{stats.domain}</div>
            <div className="text-sm text-gray-600">Domain</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderManagement;